import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';
import { useToast } from '../components/Toast.js';

interface OrgOption {
  id: string;
  name: string;
  primaryDomain: string | null;
}

interface SiteJob {
  id: string;
  org_id: string;
  job_type: string;
  status: string;
  source_url: string | null;
  target_domain: string | null;
  target_site_name: string | null;
  progress: { step?: string; detail?: string } | null;
  error_message: string | null;
  error_stage: string | null;
  claimed_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const JOB_TYPES = [
  { value: 'migrate_visual', label: 'Visual Migration (Clone)', description: 'Pixel-perfect clone of any URL using AI + Playwright', icon: '🎨' },
  { value: 'migrate_97d', label: '97 Display Migration', description: 'Data-driven rebuild from 97D SchoolModel', icon: '🔄' },
  { value: 'migrate_wordpress', label: 'WordPress Migration', description: 'Content scrape via WordPress REST API', icon: '📝' },
  { value: 'rebuild', label: 'Rebuild Existing Site', description: 'Regenerate from current CRM data', icon: '🔧' },
] as const;

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  validating: 'bg-blue-100 text-blue-600',
  queued: 'bg-blue-100 text-blue-700',
  converting: 'bg-yellow-100 text-yellow-700',
  building: 'bg-yellow-100 text-yellow-700',
  deploying: 'bg-purple-100 text-purple-700',
  live: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

type PageView = 'create' | 'list';

export function AdminMigrationsPage() {
  const [view, setView] = useState<PageView>('list');
  const { toast } = useToast();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Website Migrations</h2>
          <p className="text-sm text-gray-500">Migrate websites into the webengine platform</p>
        </div>
        <button
          onClick={() => setView(view === 'create' ? 'list' : 'create')}
          className={view === 'create'
            ? 'text-sm text-gray-500 hover:text-gray-700'
            : 'bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700'
          }
        >
          {view === 'create' ? '← Back to List' : '+ New Migration'}
        </button>
      </div>

      {view === 'create' ? (
        <CreateMigration toast={toast} onCreated={() => setView('list')} />
      ) : (
        <MigrationList />
      )}
    </div>
  );
}

// ===== CREATE MIGRATION =====

function CreateMigration({ toast, onCreated }: { toast: ReturnType<typeof useToast>['toast']; onCreated: () => void }) {
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [jobType, setJobType] = useState('migrate_visual');
  const [sourceUrl, setSourceUrl] = useState('');
  const [targetDomain, setTargetDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ organizations: OrgOption[] }>('/orgs/summary');
        setOrgs(data.organizations);
      } catch { /* */ }
      finally { setIsLoadingOrgs(false); }
    })();
  }, []);

  const needsSourceUrl = jobType !== 'rebuild';
  const selectedOrgData = orgs.find(o => o.id === selectedOrg);

  const handleSubmit = async () => {
    if (!selectedOrg) { toast('error', 'Select an organization'); return; }
    if (needsSourceUrl && !sourceUrl.trim()) { toast('error', 'Source URL is required'); return; }

    setIsSubmitting(true);
    try {
      const slug = selectedOrgData?.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) ?? 'site';
      const domain = targetDomain.trim() || `${slug}.membies.com`;

      await apiFetch('/site-jobs', {
        method: 'POST',
        body: JSON.stringify({
          org_id: selectedOrg,
          job_type: jobType,
          source_url: needsSourceUrl ? sourceUrl.trim() : undefined,
          target_domain: domain,
          target_site_name: slug,
        }),
      });
      toast('success', 'Migration started — check the list for progress');
      onCreated();
    } catch (err) {
      toast('error', `Failed to create migration: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
      {/* Step 1: Select Org */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">1. Select Organization</label>
        {isLoadingOrgs ? (
          <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
        ) : (
          <select
            value={selectedOrg}
            onChange={e => {
              setSelectedOrg(e.target.value);
              const org = orgs.find(o => o.id === e.target.value);
              if (org?.primaryDomain) setTargetDomain(org.primaryDomain);
            }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Choose an organization...</option>
            {orgs.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Step 2: Migration Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">2. Migration Type</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {JOB_TYPES.map(jt => (
            <button
              key={jt.value}
              onClick={() => setJobType(jt.value)}
              className={`p-3 rounded-lg border text-left transition-colors ${
                jobType === jt.value
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{jt.icon}</span>
                <span className="text-sm font-medium text-gray-900">{jt.label}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{jt.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Step 3: Source URL */}
      {needsSourceUrl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">3. Source URL</label>
          <input
            value={sourceUrl}
            onChange={e => setSourceUrl(e.target.value)}
            placeholder={
              jobType === 'migrate_97d' ? 'e.g., trans-mafc.com' :
              jobType === 'migrate_wordpress' ? 'e.g., https://myblog.com' :
              'e.g., https://existing-site.com'
            }
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            {jobType === 'migrate_97d'
              ? 'Enter the domain from the 97 Display platform'
              : jobType === 'migrate_wordpress'
                ? 'Enter the WordPress site URL (must have REST API enabled)'
                : 'Enter the URL of the site to clone'
            }
          </p>
        </div>
      )}

      {/* Step 4: Target Domain */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{needsSourceUrl ? '4' : '3'}. Target Domain</label>
        <input
          value={targetDomain}
          onChange={e => setTargetDomain(e.target.value)}
          placeholder="e.g., studio-name.membies.com"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-400 mt-1">Leave blank to auto-generate from org name</p>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={isSubmitting || !selectedOrg}
        className="w-full bg-primary-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Starting Migration...' : 'Start Migration'}
      </button>
    </div>
  );
}

// ===== MIGRATION LIST =====

function MigrationList() {
  const [jobs, setJobs] = useState<SiteJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});

  const fetchJobs = useCallback(async () => {
    try {
      const [jobsData, orgsData] = await Promise.all([
        apiFetch<{ jobs: SiteJob[]; total: number }>('/site-jobs?limit=50'),
        apiFetch<{ organizations: { id: string; name: string }[] }>('/orgs/summary'),
      ]);
      setJobs(jobsData.jobs);
      setTotal(jobsData.total);
      setOrgNames(Object.fromEntries(orgsData.organizations.map(o => [o.id, o.name])));
    } catch { /* */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => {
    fetchJobs();
    const timer = setInterval(fetchJobs, 10_000);
    return () => clearInterval(timer);
  }, [fetchJobs]);

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  if (jobs.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </div>
        <p className="text-sm text-gray-900 font-medium">No migrations yet</p>
        <p className="text-xs text-gray-500 mt-1">Click "New Migration" to get started.</p>
      </div>
    );
  }

  // Group by status for summary
  const statusCounts: Record<string, number> = {};
  for (const j of jobs) { statusCounts[j.status] = (statusCounts[j.status] ?? 0) + 1; }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(statusCounts).map(([status, count]) => (
          <span key={status} className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'}`}>
            {status}: {count}
          </span>
        ))}
        <span className="text-xs text-gray-400 self-center">{total} total</span>
      </div>

      {/* Job List */}
      <div className="space-y-2">
        {jobs.map(job => (
          <div key={job.id} className={`bg-white rounded-xl border p-4 ${job.status === 'failed' ? 'border-red-200' : 'border-gray-100'}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[job.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {job.status}
                  </span>
                  <span className="text-xs font-mono text-gray-400">{job.job_type.replace('migrate_', '').replace('_', ' ')}</span>
                  {job.status === 'converting' || job.status === 'building' || job.status === 'deploying' ? (
                    <svg className="w-3 h-3 text-yellow-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : null}
                </div>
                <p className="text-sm font-medium text-gray-900">{orgNames[job.org_id] ?? job.org_id.slice(0, 8)}</p>
                {job.target_domain && <p className="text-xs text-gray-500">{job.target_domain}</p>}
                {job.source_url && <p className="text-xs text-gray-400 truncate">From: {job.source_url}</p>}

                {/* Progress */}
                {job.progress?.step && (
                  <p className="text-xs text-blue-600 mt-1">
                    {String(job.progress.step)}{job.progress.detail ? `: ${String(job.progress.detail)}` : ''}
                  </p>
                )}

                {/* Error */}
                {job.error_message && (
                  <p className="text-xs text-red-500 mt-1">
                    {job.error_stage ? `[${job.error_stage}] ` : ''}{job.error_message}
                  </p>
                )}
              </div>

              <div className="text-right text-xs text-gray-400 flex-shrink-0 ml-4">
                <p>{new Date(job.created_at).toLocaleDateString()}</p>
                <p>{new Date(job.created_at).toLocaleTimeString()}</p>
                {job.completed_at && (
                  <p className="text-green-600 font-medium mt-1">
                    {Math.round((new Date(job.completed_at).getTime() - new Date(job.created_at).getTime()) / 1000)}s
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
